import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockServers } from './mockData';
import type { Server } from './types';
import ServerDetailModal from './components/ServerDetailModal';

export default function ServersPage() {
  const [servers, setServers] = useState<Server[]>(mockServers);
  const [searchNumber, setSearchNumber] = useState('');
  const [searchAlias, setSearchAlias] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'normal' | 'disabled'>('all');
  const [datacenterFilter, setDatacenterFilter] = useState('all');
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);

  const datacenters = Array.from(new Set(mockServers.map(s => s.datacenter)));

  const filteredServers = servers.filter(server => {
    if (searchNumber && !server.serverNumber.toLowerCase().includes(searchNumber.toLowerCase())) return false;
    if (searchAlias && !server.serverAlias.toLowerCase().includes(searchAlias.toLowerCase())) return false;
    if (statusFilter !== 'all' && server.status !== statusFilter) return false;
    if (datacenterFilter !== 'all' && server.datacenter !== datacenterFilter) return false;
    return true;
  });

  const handleToggleStatus = (id: string) => {
    setServers(servers.map(server =>
      server.id === id
        ? { ...server, status: server.status === 'normal' ? 'disabled' : 'normal' }
        : server
    ));
    if (selectedServer?.id === id) {
      setSelectedServer({
        ...selectedServer,
        status: selectedServer.status === 'normal' ? 'disabled' : 'normal'
      });
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">服务器管理</h1>
          <p className="text-sm text-gray-500 mt-1">管理和监控所有物理服务器资源</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">主机编号</label>
                <input
                  type="text"
                  value={searchNumber}
                  onChange={(e) => setSearchNumber(e.target.value)}
                  placeholder="搜索主机编号"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">主机别名</label>
                <input
                  type="text"
                  value={searchAlias}
                  onChange={(e) => setSearchAlias(e.target.value)}
                  placeholder="搜索主机别名"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">全部</option>
                  <option value="normal">正常</option>
                  <option value="disabled">停用</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">所属机房</label>
                <select
                  value={datacenterFilter}
                  onChange={(e) => setDatacenterFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">全部机房</option>
                  {datacenters.map(dc => (
                    <option key={dc} value={dc}>{dc}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">所属机房</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">主机编号</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">主机别名</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">状态</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">CPU</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">内存</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">硬盘</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">GPU</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredServers.map((server) => (
                  <tr key={server.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{server.datacenter}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{server.serverNumber}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{server.serverAlias}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        server.status === 'normal' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {server.status === 'normal' ? '正常' : '停用'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      <div className="font-medium">{server.cpu.available}/{server.cpu.total} 核</div>
                      <div className="text-xs text-gray-500">{server.cpu.model}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      <div className="font-medium">{server.memory.available}/{server.memory.total} GB</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      <div className="font-medium">{server.disk.available}/{server.disk.total} GB</div>
                      <div className="text-xs text-gray-500">{server.disk.type}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      <div className="font-medium">{server.gpu.available}/{server.gpu.total} 张</div>
                      <div className="text-xs text-gray-500">{server.gpu.model}</div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleStatus(server.id)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                            server.status === 'normal'
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                          }`}
                        >
                          {server.status === 'normal' ? '下线' : '上线'}
                        </button>
                        <button
                          onClick={() => setSelectedServer(server)}
                          className="px-3 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium hover:bg-teal-200 transition-colors whitespace-nowrap"
                        >
                          详情
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedServer && (
        <ServerDetailModal
          server={selectedServer}
          onClose={() => setSelectedServer(null)}
          onToggleStatus={handleToggleStatus}
        />
      )}
    </MainLayout>
  );
}
